import { useEffect, useState } from "react";
import { IconX } from "@tabler/icons-react";
import { Heading, Text } from "../../../../components/common/typography";
import { Input } from "../../../../components/common/input";
import { IconPill } from "../../../../components/common/icon-pill";
import { Button } from "../../../../components/common/button";
import { DatePicker } from "../../../../components/common/date-picker";

interface Props {
  isOpen: boolean;
  orderId: string;
  onClose: () => void;
  onConfirm: (payload: {
    shipDate: string;
    estimatedDelivery?: string;
    carrier?: string;
    trackingNumber?: string;
    trackingLink?: string;
    note?: string;
  }) => Promise<void>;
}

function toLocalDateInputValue(date: Date): string {
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60_000);
  return local.toISOString().slice(0, 10);
}

export function MarkAsShippedModal({ isOpen, orderId, onClose, onConfirm }: Props) {
  const [carrier, setCarrier] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [estimatedDelivery, setEstimatedDelivery] = useState("");
  const [shipDate, setShipDate] = useState("");
  const [trackingLink, setTrackingLink] = useState("");
  const [note, setNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7);

    setCarrier("");
    setTrackingNumber("");
    setEstimatedDelivery(toLocalDateInputValue(nextWeek));
    setShipDate(toLocalDateInputValue(tomorrow));
    setTrackingLink("");
    setNote("");
    setIsSubmitting(false);
    setError(null);
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    if (!shipDate) {
      setError("Please select a ship date.");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await onConfirm({
        shipDate,
        estimatedDelivery: estimatedDelivery || undefined,
        carrier: carrier.trim() || undefined,
        trackingNumber: trackingNumber.trim() || undefined,
        trackingLink: trackingLink.trim() || undefined,
        note: note.trim() || undefined,
      });
      onClose();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to mark order as shipped. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDevAutofill = () => {
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7);

    setCarrier("DHL");
    setTrackingNumber("1Z999AA10123456784");
    setEstimatedDelivery(toLocalDateInputValue(nextWeek));
    setShipDate(toLocalDateInputValue(tomorrow));
    setTrackingLink("https://track.dhl.com/1Z999AA10123456784");
    setNote("Handed over to DHL — use the tracking link to follow delivery.");
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:items-center sm:p-6"
      onClick={onClose}
    >
      <div
        className="my-auto flex max-h-[calc(100vh-2rem)] w-full max-w-[610px] flex-col rounded-2xl bg-bgWhite shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >

        <div className="flex items-center justify-between p-6 pb-0">
          <Heading variant="h3" tone="dark">Mark as Shipped — {orderId}</Heading>
          <div className="flex items-center gap-2">
            {import.meta.env.DEV && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDevAutofill}
                disabled={isSubmitting}
              >
                Dev autofill
              </Button>
            )}
            <IconPill
              as="button"
              size={32}
              tone="ghost"
              aria-label="Close"
              onClick={onClose}
              className="!text-textMuted hover:!bg-bgLight hover:!text-textDark"
            >
              <IconX size={18} />
            </IconPill>
          </div>
        </div>

        <div className="mt-5 border-t border-borderLight" />

        <div className="flex flex-col gap-5 overflow-y-auto px-6 py-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Text variant="bodyBold" tone="dark">Carrier / Courier</Text>
              <Input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="e.g. DHL, Aramex, FedEx" className="placeholder:!text-textMuted" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Text variant="bodyBold" tone="dark">Tracking Number</Text>
              <Input value={trackingNumber} onChange={(e) => setTrackingNumber(e.target.value)} placeholder="e.g. 1Z999AA10123456784" className="placeholder:!text-textMuted" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-1.5">
              <Text variant="bodyBold" tone="dark">Estimated Delivery Date</Text>
              <DatePicker
                value={estimatedDelivery}
                onChange={setEstimatedDelivery}
                min={toLocalDateInputValue(new Date())}
                size="lg"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Text variant="bodyBold" tone="dark">Ship Date</Text>
              <DatePicker
                value={shipDate}
                onChange={setShipDate}
                min={toLocalDateInputValue(new Date())}
                size="lg"
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Text variant="bodyBold" tone="dark">Tracking Link</Text>
            <Input value={trackingLink} onChange={(e) => setTrackingLink(e.target.value)} placeholder="https://track.carrier.com/..." className="placeholder:!text-textMuted" disabled={isSubmitting} />
          </div>

          <div className="flex flex-col gap-1.5">
            <Text variant="bodyBold" tone="dark">Note to Buyer (Optional)</Text>
            <Input
              multiline
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Your order has been handed to DHL. Use the tracking link to follow delivery."
              className="placeholder:!text-textMuted"
              disabled={isSubmitting}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 border-t border-borderLight px-6 pb-6 pt-5">
          <Button
            variant="primary"
            size="md"
            fullWidth
            className="!bg-textDark !border-textDark"
            onClick={handleConfirm}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Confirm Shipment"}
          </Button>
          <Button variant="secondary" size="md" fullWidth onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
        </div>
        {error && <Text variant="bodyXs" className="px-6 pb-4 text-accentError">{error}</Text>}
      </div>
    </div>
  );
}
